import { createContext, useContext, useReducer, useEffect } from 'react'
import { authService, getAuthToken } from '../services/authService'

const AuthContext = createContext()

const initialState = {
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
    role: null,
    permissions: [],
    error: null
}

function authReducer(state, action) {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload }
        case 'LOGIN_START':
            return { ...state, isLoading: true, error: null }
        case 'LOGIN_SUCCESS':
            return {
                ...state,
                user: action.payload.user,
                token: action.payload.token,
                isAuthenticated: true,
                isLoading: false,
                role: action.payload.user.role,
                permissions: action.payload.permissions || [],
                error: null
            }
        case 'LOGIN_FAILURE':
            return {
                ...state,
                user: null,
                token: null,
                isAuthenticated: false,
                isLoading: false,
                role: null,
                permissions: [],
                error: action.payload
            }
        case 'LOGOUT':
            return {
                ...state,
                user: null,
                token: null,
                isAuthenticated: false,
                isLoading: false,
                role: null,
                permissions: [],
                error: null
            }
        case 'UPDATE_USER':
            return { ...state, user: { ...state.user, ...action.payload } }
        case 'SET_ERROR':
            return { ...state, error: action.payload }
        case 'CLEAR_ERROR':
            return { ...state, error: null }
        default:
            return state
    }
}

export function AuthProvider({ children }) {
    const [state, dispatch] = useReducer(authReducer, initialState)

    useEffect(() => {
        const initAuth = async () => {
            try {
                dispatch({ type: 'SET_LOADING', payload: true })
                const token = getAuthToken()
                const userDataString = sessionStorage.getItem('userData')

                if (token && userDataString) {
                    const userData = JSON.parse(userDataString)
                    dispatch({
                        type: 'LOGIN_SUCCESS',
                        payload: {
                            user: userData,
                            token: token,
                            permissions: userData.permissions || []
                        }
                    })
                } else {
                    dispatch({ type: 'LOGIN_FAILURE', payload: null })
                }
            } catch (error) {
                sessionStorage.removeItem('authToken')
                sessionStorage.removeItem('userData')
                dispatch({ type: 'LOGIN_FAILURE', payload: 'Session expired' })
            }
        }
        initAuth()
    }, [])

    const login = async (credentials) => {
        try {
            dispatch({ type: "LOGIN_START" })
            const result = await authService.login(credentials)

            if (!result.success) {
                dispatch({ type: "LOGIN_FAILURE", payload: result.error })
                return result
            }

            if (result.data?.role !== 'company' && result.data?.role !== 'admin') {
                dispatch({ type: "LOGIN_FAILURE", payload: "Only company accounts are supported." })
                return { success: false, error: "Only company accounts are supported." }
            }

            const user = result.data
            const token = result.token

            sessionStorage.setItem('authToken', JSON.stringify({ token, timestamp: Date.now() }))
            sessionStorage.setItem('userData', JSON.stringify(user))
            dispatch({
                type: "LOGIN_SUCCESS",
                payload: { user, token, permissions: user.permissions || [] }
            })

            return result

        } catch (err) {
            dispatch({ type: "LOGIN_FAILURE", payload: err.message })
            return { success: false, error: err.message }
        }
    }

    const loginWithToken = async (token) => {
        try {
            dispatch({ type: "LOGIN_START" })
            const tokenData = { token: token, timestamp: Date.now() }
            sessionStorage.setItem('authToken', JSON.stringify(tokenData))
            const user = await authService.getCurrentUser()

            if (user) {
                if (user.role !== 'company' && user.role !== 'admin') {
                    dispatch({ type: "LOGIN_FAILURE", payload: "Only company accounts are supported." })
                    return { success: false, error: "Only company accounts are supported." }
                }

                dispatch({
                    type: "LOGIN_SUCCESS",
                    payload: { user, token, permissions: user.permissions || [] }
                })
                return { success: true, data: user }
            } else {
                dispatch({ type: "LOGIN_FAILURE", payload: "Failed to verify token" })
                return { success: false, error: "Failed to verify token" }
            }

        } catch (err) {
            dispatch({ type: "LOGIN_FAILURE", payload: err.message })
            return { success: false, error: err.message }
        }
    }

    const register = async (userData) => {
        try {
            dispatch({ type: 'LOGIN_START' })
            // Enforce company role
            const dataWithRole = { ...userData, role: 'company' }
            const result = await authService.register(dataWithRole)

            if (!result.success) {
                dispatch({ type: 'LOGIN_FAILURE', payload: result.error })
                return result
            }

            const user = result.data
            const token = result.token

            sessionStorage.setItem('authToken', JSON.stringify({ token, timestamp: Date.now() }))
            sessionStorage.setItem('userData', JSON.stringify(user))

            dispatch({
                type: 'LOGIN_SUCCESS',
                payload: { user, token, permissions: user.permissions || [] }
            })

            return result
        } catch (error) {
            dispatch({ type: 'LOGIN_FAILURE', payload: error.message })
            return { success: false, error: error.message }
        }
    }

    const logout = () => {
        sessionStorage.removeItem('authToken')
        sessionStorage.removeItem('userData')
        dispatch({ type: 'LOGOUT' })
    }

    const updateUser = (userData) => {
        const updatedUser = { ...state.user, ...userData }
        sessionStorage.setItem('userData', JSON.stringify(updatedUser))
        dispatch({ type: 'UPDATE_USER', payload: userData })
    }

    const refreshUser = async () => {
        try {
            const userData = await authService.getCurrentUser()
            if (userData) {
                sessionStorage.setItem('userData', JSON.stringify(userData))
                dispatch({ type: 'UPDATE_USER', payload: userData })
                return userData
            }
            return null
        } catch (error) {
            return null
        }
    }

    const clearError = () => dispatch({ type: 'CLEAR_ERROR' })

    const isCompany = () => state.user?.role === 'company'
    const hasPermission = (permission) => state.permissions.includes(permission)
    const getAuthHeaders = () => {
        if (!state.token) return {}
        return { 'Authorization': `Bearer ${state.token}`, 'Content-Type': 'application/json' }
    }

    const value = {
        ...state,
        login,
        loginWithToken,
        register,
        logout,
        updateUser,
        refreshUser,
        clearError,
        isCompany,
        hasPermission,
        getAuthHeaders
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (!context) throw new Error('useAuth must be used within an AuthProvider')
    return context
}

export default AuthContext
